import React from "react";
import { useAppearance } from "../../context/AppearanceContext";

// Static, tasteful, screen-edge seasonal decorations (Christmas lights, Halloween cobwebs, spring
// blossom, autumn leaves, etc.). Cosmetic and non-interactive. Suppressed when decorative effects
// are off or reduce-motion is on (motion parts). Sits at the very top edge so it never covers content.
const DECOR = {
  christmas: "lights", gingerbread: "lights", halloween: "cobweb", pride: "ribbon",
  winter: "icicles", spring: "blossom", autumn: "leaves", summer: "bunting",
};
const LIGHT_COLOURS = {
  christmas: ["#E23B3B", "#2E7D32", "#F4A72C", "#2D6BE0", "#8E44AD", "#F5E6C8"],
  gingerbread: ["#D9534F", "#E8A13C", "#F4C6D0", "#7ABF9E", "#F5E6C8"],
};

const Cobweb = ({ className }) => (
  <svg className={className} viewBox="0 0 100 100" fill="none" stroke="rgba(120,120,140,0.85)" strokeWidth="1">
    <path d="M0 0 L100 100 M0 0 L70 0 M0 0 L0 70 M0 0 L100 40 M0 0 L40 100" />
    <path d="M0 0 Q28 10 20 28 Q10 40 30 46 Q18 60 40 66 Q30 80 52 84" />
    <path d="M0 0 Q10 28 28 20 Q40 10 46 30 Q60 18 66 40 Q80 30 84 52" />
  </svg>
);

export default function SeasonalDecor() {
  const { theme, prefs } = useAppearance();
  if (!prefs.ambient) return null;
  const kind = DECOR[theme?.id];
  if (!kind) return null;

  if (kind === "lights") {
    const cols = LIGHT_COLOURS[theme.id] || LIGHT_COLOURS.christmas;
    const N = 26, waves = 8, amp = 9, base = 16;
    const wireY = (f) => base + amp * Math.sin(f * waves * Math.PI * 2);
    let d = `M0 ${wireY(0).toFixed(1)}`;
    for (let s = 1; s <= 140; s++) { const f = s / 140; d += ` L${(f * 1000).toFixed(1)} ${wireY(f).toFixed(1)}`; }
    return (
      <div className="pl-decor" data-testid="seasonal-decor" data-decor="lights">
        <div className="pl-lights2">
          <svg className="pl-wire" viewBox="0 0 1000 60" preserveAspectRatio="none" aria-hidden="true">
            <path d={d} fill="none" stroke="#1f2937" strokeWidth="1.4" strokeLinecap="round" />
          </svg>
          {Array.from({ length: N }).map((_, i) => {
            const f = (i + 0.5) / N;
            return (
              <span key={i} className="pl-bulb2"
                style={{ left: `${(f * 100).toFixed(2)}%`, top: `${(wireY(f) + 6).toFixed(1)}px`, "--b": cols[i % cols.length], animationDelay: `${(i % 5) * 0.35}s` }} />
            );
          })}
        </div>
      </div>
    );
  }
  if (kind === "cobweb") {
    return (
      <div className="pl-decor" data-testid="seasonal-decor" data-decor="cobweb">
        <Cobweb className="pl-web" /><Cobweb className="pl-web r" />
      </div>
    );
  }
  if (kind === "ribbon") {
    return (
      <div className="pl-decor" data-testid="seasonal-decor" data-decor="ribbon">
        <div className="pl-strip" style={{ backgroundImage: "linear-gradient(90deg,#E40303,#FF8C00,#FFED00,#008026,#004DFF,#750787)" }} />
      </div>
    );
  }
  if (kind === "icicles") {
    return (
      <div className="pl-decor" data-testid="seasonal-decor" data-decor="icicles">
        <svg viewBox="0 0 1200 24" preserveAspectRatio="none" className="absolute left-0 right-0 top-0 h-4 w-full" style={{ filter: "drop-shadow(0 1px 1px rgba(120,160,190,.25))" }}>
          <path d="M0 0 H1200 V4 L1180 4 L1170 16 L1160 4 L1120 4 L1108 20 L1096 4 L1050 4 L1040 12 L1030 4 L980 4 L968 18 L956 4 L900 4 L892 10 L884 4 L820 4 L808 22 L796 4 L740 4 L732 12 L724 4 L660 4 L648 18 L636 4 L580 4 L572 10 L564 4 L500 4 L488 20 L476 4 L420 4 L412 12 L404 4 L340 4 L328 18 L316 4 L260 4 L252 10 L244 4 L180 4 L168 20 L156 4 L100 4 L92 12 L84 4 L20 4 L10 14 L0 4 Z" fill="#dbeaf4" opacity="0.9" />
        </svg>
      </div>
    );
  }
  // blossom / leaves / bunting: hanging garland
  const items = kind === "blossom"
    ? ["#F7C6D9", "#A8C686", "#FFE680", "#B8A7D9"]
    : kind === "leaves"
      ? ["#C96C2D", "#D4A017", "#7A8450", "#6F4E37"]
      : ["#4DA8DA", "#FFD54F", "#FF8A65", "#7FD8BE"];
  return (
    <div className="pl-decor" data-testid="seasonal-decor" data-decor={kind}>
      <div className="pl-garland">
        {Array.from({ length: 22 }).map((_, i) => (
          <span key={i} style={{ left: `${(i / 21) * 100}%`, animationDelay: `${(i % 4) * 0.5}s` }}>
            {kind === "bunting"
              ? <span style={{ display: "inline-block", width: 0, height: 0, borderLeft: "9px solid transparent", borderRight: "9px solid transparent", borderTop: `14px solid ${items[i % 4]}` }} />
              : <span style={{ display: "inline-block", width: 13, height: kind === "leaves" ? 16 : 13, background: items[i % 4], borderRadius: kind === "leaves" ? "0 60% 0 60%" : "50%", boxShadow: "0 1px 2px rgba(0,0,0,.12)" }} />}
          </span>
        ))}
      </div>
    </div>
  );
}
